import { isMacAppStore } from '@/lib/appDistribution';
// Shared by the page and legacy modal entry points. Keep old deep links working.
export function firstSettingsParam(value) {
  return Array.isArray(value) ? value[0] : value;
}

export function isSettingsIndexSection(value) {
  const section = firstSettingsParam(value);
  return !section || section === 'index' || section === 'main';
}

export function normalizeSettingsSection(value) {
  const section = firstSettingsParam(value);
  return ({ profile: 'account', main: 'appearance', billing: 'subscription', 'default-model': 'general', 'remote-projects': 'remote' })[section] || section || 'appearance';
}

export function settingsEntrySection(modalName, props = {}) {
  const explicit = props.tab || props.initialScreen;
  if (explicit) return explicit;
  // The page resolves this to the overview on phones and the first panel on desktop.
  return 'index';
}

export function hasEarnTasks(earnPrograms) {
  return earnPrograms === null || earnPrograms.some(program => program.key !== 'win_back' && program.isAvailable);
}

export function getSettingsSections({ isGhost, isPlus, isAffiliatePartner = false, platform, earnPrograms = null, keepEarn = false }, t) {
  // Unknown status stays accessible for loading/retry. Win-back bonuses have no
  // task in EarnCreditsSettings, so they must not keep an empty menu visible.
  // keepEarn holds an open panel in place after its last claim.
  const showEarn = keepEarn || hasEarnTasks(earnPrograms);
  const section = (id, label, icon, group) => ({ id, title: t(`settingsWeb.${label}`), icon, group, description: t(`settingsPage.descriptions.${id}`), searchTerms: t(`settingsPage.searchTerms.${id}`) });
  return [
    {
      ...section('appearance', 'appearance', 'palette', 'preferences'),
      searchTerms: [
        t('settingsPage.searchTerms.appearance'),
        t('settings.appearance.showResponseImages.title'),
        t('settings.appearance.showResponseImages.subtitle'),
        ...(platform === 'macos' ? [
          t('settings.appearance.fontSize.title'),
          t('settings.appearance.fontSize.subtitle'),
        ] : []),
        ...(['ios', 'android'].includes(platform) ? [
          t('settings.appearance.keepScreenAwake.title'),
          t('settings.appearance.keepScreenAwake.subtitle'),
        ] : []),
      ].join(' '),
    },
    ...(platform === 'macos' && !isGhost && !isMacAppStore() ? [
      section('mac', 'macApp', 'laptop', 'preferences'),
      section('remote', 'remoteAccess', 'mobile-screen', 'preferences'),
    ] : []),
    section('general', 'general', 'gear', 'preferences'),
    ...(['ios', 'android'].includes(platform) ? [{ id: 'notifications', title: 'Notifications', icon: 'bell', group: 'preferences',
      description: 'Manage alerts, sounds and notification permissions.', searchTerms: 'notifications alerts push permission sounds enable disable' }] : []),
    ...(!isGhost ? [
      ...(['ios', 'android'].includes(platform) ? [{ id: 'remote', title: 'Connect to your computer', icon: 'laptop', group: 'preferences',
        description: 'Use folders on your PC or Mac from your phone with Remote.', searchTerms: 'remote windows pc mac computer connect pair projects folders' }] : []),
      section('personalization', 'personalization', 'sliders', 'preferences'),
      section('memory', 'memory', 'brain', 'preferences'),
      { id: 'plugins', title: 'Plugins', icon: 'grid-2', group: 'tools',
        description: 'Add skills and connected apps to Work.', searchTerms: 'plugins skills connections mcp oauth apps integrations' },
      ...(['web', 'macos'].includes(platform) ? [section('keyboard', 'keyboardShortcuts', 'keyboard', 'preferences')] : []),
      section('account', 'account', 'user-shield', 'account'),
      {
        ...section('subscription', isPlus ? 'billing' : 'upgrade', 'credit-card', 'account'),
        description: t(`settingsPage.descriptions.${isPlus ? 'subscription' : 'upgrade'}`),
      },
      { ...section('credits', 'credits', 'coins', 'account'),
        ...(isMacAppStore() ? { description: 'See your balance and add credits with the App Store.', searchTerms: 'credits balance buy app store purchases' } : {}) },
      section('usage', 'usageHistory', 'chart-line', 'account'),
      ...(platform === 'web' ? [{ id: 'business', title: 'Gab AI Business', icon: 'building', group: 'account',
        description: 'Manage your team, shared credits, and company billing.', searchTerms: 'business company team members invitations seats pooled credits limits' }] : []),
      ...(isPlus && showEarn ? [section('earn', 'earn', 'gift', 'account')] : []),
      section('referral', 'referAFriend', 'user-plus', 'account'),
      ...(isAffiliatePartner ? [section('affiliate', 'affiliate', 'percent', 'account')] : []),
      section('import', 'importLabel', 'file-import', 'tools'),
      section('api', 'apiSettings', 'code-fork', 'tools'),
    ] : []),
    section('help', 'help', 'question-circle', 'tools'),
  ];
}
